import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import _ from 'lodash';

import {
  MAX_QUICK_ACTIONS_PER_TAB,
  MAX_RESULTS_PER_CATEGORY,
  MAX_SUGGESTED_PER_CATEGORY,
  SEARCH_DEBOUNCE_DELAY_MS,
  SearchCategory,
  SLASH_COMMAND_PREFIX,
} from '../UniversalSearch.constants';
import {
  diverseTruncate,
  filterSearchResults,
  groupResultsByCategory,
  pickDiverseFields,
  sortSuggestedItems,
} from '../UniversalSearch.search.utils';

import useSearchIndex from './useSearchIndex';

const useSearchResults = ({ initialQuery = '' } = {}) => {
  const [query, setQuery] = useState(initialQuery);
  const {
    tab: currentTabRoute,
    subtab: currentSubtab,
    entityId: currentEntityId,
    section: currentSection,
    subsection: currentSubsection,
  } = useParams();
  const { searchIndex, shortcutEntries } = useSearchIndex();

  const filteredResults = useMemo(
    () => filterSearchResults({ items: searchIndex, query, currentTabRoute, currentSubtab }),
    [searchIndex, query, currentTabRoute, currentSubtab],
  );

  const groupedResults = useMemo(() => groupResultsByCategory(filteredResults), [filteredResults]);

  const trimmedQuery = query?.trim() || '';
  const isSlashCommand = trimmedQuery.startsWith(SLASH_COMMAND_PREFIX);

  const truncatedGroupedResults = useMemo(
    () =>
      _.mapValues(groupedResults, (categoryItems, category) => {
        if (isSlashCommand && category === SearchCategory.ACTIONS) {
          return categoryItems;
        }

        return category === SearchCategory.RECORDS
          ? diverseTruncate({ items: categoryItems, limit: MAX_RESULTS_PER_CATEGORY })
          : categoryItems.slice(0, MAX_RESULTS_PER_CATEGORY);
      }),
    [groupedResults, isSlashCommand],
  );

  const flatResults = useMemo(
    () =>
      Object.values(truncatedGroupedResults).reduce(
        (acc, categoryItems) => [...acc, ...categoryItems],
        [],
      ),
    [truncatedGroupedResults],
  );

  const suggestedItems = useMemo(() => {
    const grouped = _.groupBy(searchIndex, 'category');

    return Object.values(SearchCategory).reduce((acc, category) => {
      const items = grouped[category] || [];

      const baseFiltered =
        category === SearchCategory.NAVIGATION && currentTabRoute
          ? items.filter(item => !(item.isTabLink && item.tabRoute === currentTabRoute))
          : items;

      const quickActions =
        category === SearchCategory.NAVIGATION && currentTabRoute
          ? baseFiltered
              .filter(item => {
                if (!item.isQuickAction || item.tabRoute !== currentTabRoute) {
                  return false;
                }

                if (currentSubtab) {
                  if (!_.isNil(item.financialRecordId)) {
                    return !currentSection;
                  }

                  return (
                    item.quickActionContext === currentSubtab ||
                    item.quickActionContext === currentEntityId ||
                    item.quickActionContext === currentSubsection
                  );
                }

                return _.isNil(item.quickActionContext);
              })
              .slice(0, MAX_QUICK_ACTIONS_PER_TAB)
          : [];

      const filtered =
        category === SearchCategory.NAVIGATION && currentTabRoute
          ? baseFiltered.filter(item => {
              if (quickActions.includes(item)) {
                return false;
              }

              if (!_.isNil(item.quickActionContext) && item.quickActionContext !== currentSubtab) {
                return false;
              }

              if (currentSubtab && !_.isNil(item.entityType) && item.entityType !== currentSubtab) {
                return false;
              }

              if (
                currentSubtab &&
                item.entityType === currentSubtab &&
                _.isNil(item.quickActionContext)
              ) {
                return false;
              }

              if (
                currentEntityId &&
                item.borrowerEntityId === currentEntityId &&
                _.isNil(item.quickActionContext)
              ) {
                return false;
              }

              if (
                currentSubtab &&
                item.isQuickAction &&
                _.isNil(item.quickActionContext) &&
                item.tabRoute === currentTabRoute
              ) {
                return false;
              }

              if (
                currentSubsection &&
                item.financialRecordId === currentSubsection &&
                !_.isNil(item.quickActionContext)
              ) {
                return false;
              }

              if (
                currentSection &&
                !_.isNil(item.financialRecordId) &&
                item.tabRoute === currentTabRoute
              ) {
                return false;
              }

              return true;
            })
          : baseFiltered;

      const sorted = sortSuggestedItems({ filtered, category, currentTabRoute });

      const picked =
        category === SearchCategory.FIELDS
          ? pickDiverseFields(sorted, MAX_SUGGESTED_PER_CATEGORY)
          : sorted.slice(0, MAX_SUGGESTED_PER_CATEGORY);

      return {
        ...acc,
        [category]: category === SearchCategory.NAVIGATION ? [...quickActions, ...picked] : picked,
      };
    }, {});
  }, [
    searchIndex,
    currentTabRoute,
    currentSubtab,
    currentEntityId,
    currentSection,
    currentSubsection,
  ]);

  const flatSuggestedItems = useMemo(
    () =>
      Object.values(SearchCategory).reduce(
        (acc, category) => [...acc, ...(suggestedItems[category] || [])],
        [],
      ),
    [suggestedItems],
  );

  const onQueryChange = useMemo(
    () =>
      _.debounce(value => {
        setQuery(value);
      }, SEARCH_DEBOUNCE_DELAY_MS),
    [],
  );

  return {
    query,
    setQuery,
    onQueryChange,
    groupedResults: truncatedGroupedResults,
    flatResults,
    flatSuggestedItems,
    totalResultsCount: filteredResults.length,
    shortcutEntries,
    suggestedItems,
  };
};

export default useSearchResults;
